import React, { Component } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import moment from 'moment';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import BootstrapTable from 'react-bootstrap-table-next';
import Pagination from 'components/v2/Pagination';
import { titleize, humanize } from 'underscore.string';
import { withTranslation } from 'react-i18next';

const colors = {
  available: 'bg-green',
  unavailable: 'bg-red-500',
  'In Progress': 'bg-teal-400',
  accepted: 'green',
  'Awaiting Booking': 'bg-purple-500',
};

const MAX_BUTTONS = 15;

const headerFormatter = (column, colIndex, { sortElement }) => {
  return (
    <div className="text-sm text-grey-darker">
      <strong>{column.text}</strong>
      {sortElement}
    </div>
  );
};

const statusFormatter = (cell, row) => {
  const bg = colors[cell] || 'bg-green';

  const classNames = classnames({
    'p-1': true,
    rounded: true,
    [bg]: true,
    'text-white': true,
    'text-sm': true,
    'font-bold': true,
  });
  return <span className={classNames}>{titleize(humanize(cell))}</span>;
};

const dateFormatter = (cell) => <span>{moment(cell).format('L')}</span>;

const booleanFormatter = (cell, row) => {
  let missingDocs = false;
  if (typeof row.missing_docs !== 'undefined') {
    missingDocs = row.missing_docs;
  }
  return <span>{missingDocs ? 'True' : 'False'}</span>;
};

const priceFormatter = (cell) => <span>${cell}</span>;

const entityFormatter = (cell) => (
  <div className="block overflow-hidden text-overflow-ellipsis">
    <a className="">{cell}</a>
  </div>
);

const objectIdTimestampFormatter = (cell, row) => {
  const dateFromObjectId = function(objectId) {
    return new Date(parseInt(objectId.substring(0, 8), 16) * 1000);
  };

  return <span>{moment(dateFromObjectId(row._id)).format('YYYY-MM-DD')}</span>;
};

const customerFormatter = (type) => (cell, row) => {
  if (!cell) return null;
  let customer;
  let _id;

  switch (type) {
    case 'shipper':
      customer = cell;
      break;
    case 'carrier':
      customer = cell;
      if (!row.carrier_id) {
        return null;
      }
      _id =
        typeof row.carrier_id === 'string'
          ? row.carrier_id
          : row.carrier_id._id;
      break;
    default:
      customer = cell;
  }

  return (
    <div>
      <a className="" href={`/users/${_id}`} target="_blank" rel="noreferrer">
        {customer}
      </a>
    </div>
  );
};

const locationFormatter = (cell) => {
  if (!cell) return null;

  return (
    <div>
      <div>
        <label className="mr-2 text-sm font-bold text-grey-darker">
          {cell.name}
        </label>
      </div>
      <span className="mb-4">{cell.address}</span>
    </div>
  );
};

const referenceNumberFormatter = (cell) => {
  const filtered = cell.filter((reference) => {
    return reference.name === 'Master Bill Of Lading';
  });

  const referenceNumbers = filtered.map((refNum) => (
    <div className="flex flex-col mb-2">
      <span className="mb-4">{refNum.value}</span>
    </div>
  ));

  return <div>{referenceNumbers}</div>;
};

const expandFormatter =
  (toggleExpand, buttonText, isStaging, removeShipment, t) => (cell, row) => {
    return (
      <div className="flex flex-row">
        <button
          className="button bg-blue-cm hover:bg-blue-cm-darker text-white mr-2"
          onClick={() => {
            toggleExpand();
          }}
        >
          {buttonText || t('EDIT')}
        </button>
        {/* this is super hacky but there isn't any better way to do this with DataList */}
        {isStaging && (
          <button
            className="button bg-grey-dark hover:bg-grey-darker text-white"
            onClick={() => {
              removeShipment();

              // TODO: this will be a separate flux action eventually
            }}
          >
            X
          </button>
        )}
      </div>
    );
  };

class DataListAlt extends Component {
  static propTypes = {
    columns: PropTypes.array,
    select: PropTypes.bool,
  };

  state = {
    expanded: false,
    showEditEquipmentModal: false,
  };

  getEntityFormatter = (type) => {
    switch (type) {
      case 'location':
        return this.createResourceFormatter('locations');
      case 'order':
        return this.createResourceFormatter('orders');
      case 'shipment':
        return this.createResourceFormatter('shipments');
      case 'equipment':
        return this.createResourceFormatter('equipment');
      default:
        return entityFormatter;
    }
  };

  createResourceFormatter = (resource) => (cell, row) => {
    if (resource === 'equipment') {
      return (
        <div
          className="block"
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          {cell.map((eq) => {
            return (
              <span
                style={{ color: '#337ab7', cursor: 'pointer' }}
                onClick={() => this.props.onEquipmentClick(eq)}
              >
                {eq.equipment_id}
              </span>
            );
          })}
        </div>
      );
    }
    const link = `/${resource}/${row._id}`;
    return (
      <div className="block overflow-hidden text-overflow-ellipsis">
        <a
          className=""
          title={cell}
          href={link}
          target="_blank"
          rel="noreferrer"
        >
          {cell}
        </a>
      </div>
    );
  };

  toggleExpand = () => {
    this.setState({ expanded: !this.state.expanded });
  };

  formatColumns() {
    let columns = this.props.columns.map((column) => {
      if (!this.props.showHeaders) {
        column.headerStyle = { display: 'none' };
      } else {
        // Somewhere deep, this impacts rendering multiple data list with
        // header styles
        if (column.headerStyle && column.headerStyle.display) {
          delete column.headerStyle.display;
        }
      }

      if (this.props.noBorders) {
        column.style = { border: 0 };
      }

      if (!column.headerFormatter) {
        column.headerFormatter = headerFormatter;
      }

      if (!column.formatter) {
        switch (column.type) {
          case 'price':
            column.formatter = priceFormatter;
            break;
          case 'entity':
            column.formatter = this.getEntityFormatter(column.entityType);
            break;
          case 'customer':
            column.formatter = customerFormatter(column.entityType);
            break;
          case 'boolean':
            column.formatter = booleanFormatter;
            break;
          case 'datetime':
            column.formatter = dateFormatter;
            break;
          case 'reference_number':
            column.formatter = referenceNumberFormatter;
            break;
          case 'location':
            column.formatter = locationFormatter;
            break;
          case 'status':
            column.formatter = statusFormatter;
            break;
          case 'objectId':
            column.formatter = objectIdTimestampFormatter;
            break;
        }
      }

      return column;
    });
    if (this.props.expandable) {
      columns.push({
        dataField: '',
        formatter: expandFormatter(
          this.toggleExpand,
          this.props.expandButtonText,
          this.props.isStaging,
          this.props.remove,
          this.props.t,
        ),
        headerStyle: { display: 'none' },
        style: {
          borderTop: this.props.showHeaders ? '2px solid gainsboro' : '0px',
        },
      });
    }
    return columns;
  }

  componentWillReceiveProps(newProps) {
    if (newProps.editingCanceled && !this.props.editingCanceled) {
      this.setState({ expanded: false });
    }
  }

  render() {
    const columns = this.formatColumns();
    const selectRow = this.props.select
      ? {
        mode: 'checkbox',
        selected: this.props.selected,
        onSelect: this.props.handleOnSelect,
        onSelectAll: this.props.handleOnSelectAll,
        hideSelectColumn: this.props.hideSelectColumn,
        clickToSelect: this.props.clickToSelect,
      }
      : undefined;

    const rowStyle = null;
    const { t } = this.props;
    return (
      <div>
        {this.props.paginate && (
          <Pagination
            first
            last
            total={this.props.total}
            page={this.props.page}
            onPageClick={this.props.onPaginationSelect}
            perPage={this.props.perPage}
          />
        )}
        <BootstrapTable
          keyField="_id"
          striped={this.props.striped}
          data={this.props.data}
          columns={columns}
          selectRow={selectRow}
          bordered={false}
          noDataIndication={this.props.noDataIndication || t('NO_DATA_FOUND')}
          rowStyle={rowStyle}
        />
        {this.props.children
          ? this.props.children(this.state.expanded, this.toggleExpand)
          : null}
        {this.props.paginate && (
          <Pagination
            first
            last
            total={this.props.total}
            page={this.props.page}
            onPageClick={this.props.onPaginationSelect}
            perPage={this.props.perPage}
          />
        )}
      </div>
    );
  }
}

export default withTranslation()(DataListAlt);

